import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import FlexRow from "../../../../components/base/FlexRow";


export type WalletDateRange = {
    start: string   // yyyy-MM-dd
    end: string     // yyyy-MM-dd
    label: string
}

type Option = {
    value: string
    label: string
    range?: () => WalletDateRange
}

const fmt = (m: moment.Moment) => m.format("YYYY-MM-DD")

// Date presets, in the order requested: current/last week, the current month and the 5
// previous months, current/last year, then a custom range.
const buildOptions = (): Option[] => {
    const opts: Option[] = []

    opts.push({
        value: "cw", label: "Current Week",
        range: () => ({ start: fmt(moment().startOf("isoWeek")), end: fmt(moment().endOf("day")), label: "Current Week" })
    })
    opts.push({
        value: "lw", label: "Last Week",
        range: () => ({ start: fmt(moment().subtract(1, "week").startOf("isoWeek")), end: fmt(moment().subtract(1, "week").endOf("isoWeek")), label: "Last Week" })
    })

    for (let i = 0; i < 6; i++) {
        const m = moment().subtract(i, "months")
        const label = m.format("MMMM YYYY")
        opts.push({
            value: `m:${m.format("YYYY-MM")}`, label,
            range: () => ({ start: fmt(m.clone().startOf("month")), end: fmt(m.clone().endOf("month")), label })
        })
    }

    const year = moment().year()
    opts.push({
        value: "cy", label: `Current Year (${year})`,
        range: () => ({ start: fmt(moment().startOf("year")), end: fmt(moment().endOf("day")), label: `Current Year (${year})` })
    })
    opts.push({
        value: "ly", label: `Last Year (${year - 1})`,
        range: () => ({ start: fmt(moment().subtract(1, "year").startOf("year")), end: fmt(moment().subtract(1, "year").endOf("year")), label: `Last Year (${year - 1})` })
    })

    opts.push({ value: "custom", label: "Custom Date" })

    return opts
}


type Props = {
    onChange: (range: WalletDateRange) => void
    // Where the last choice is remembered; each page that uses the filter keeps its own.
    // Pass null to always open on the default preset.
    storageKey?: string | null
    // Preset used when nothing is remembered: "cw", "cm" (current month), or any option value.
    defaultId?: string
    // DOM id the custom date-range picker binds to; must be unique on the page.
    elementId?: string
}

const DEFAULT_STORAGE_KEY = "wallet_overview_date"

type Saved = { id: string; range?: WalletDateRange }

const readSaved = (key: string | null): Saved | undefined => {
    if (!key) return undefined
    try {
        const raw = localStorage.getItem(key)
        return raw ? JSON.parse(raw) as Saved : undefined
    } catch { return undefined }
}

const writeSaved = (key: string | null, saved: Saved) => {
    if (!key) return
    try { localStorage.setItem(key, JSON.stringify(saved)) } catch { }
}


// Shared date-range filter for the Wallet Overview. Owns the selection and reports the resolved
// range up so every widget on the tab can react to the same period. Remembers the last choice.
export default function WalletDateFilter({ onChange, storageKey = DEFAULT_STORAGE_KEY, defaultId = "cw", elementId = "wallet-chart-date" }: Props) {

    const options = useMemo(buildOptions, [])

    // "cm" is shorthand for the current month's preset.
    const fallbackId = defaultId === "cm" ? `m:${moment().format("YYYY-MM")}` : defaultId

    // Restore the previous choice, but only if it's still a valid option (month presets age out).
    const saved = useMemo(() => readSaved(storageKey), [storageKey])
    const initialId = saved && options.some(o => o.value === saved.id) ? saved.id : fallbackId

    const [dateId, dateIdSet] = useState<string>(initialId)
    const [custom, customSet] = useState<WalletDateRange | undefined>(
        initialId === "custom" ? saved?.range : undefined
    )

    // Runs on mount too (dateId is restored), so the resolved range is emitted immediately.
    useEffect(() => {
        if (dateId === "custom") {
            (window as any).daterangecustom?.(elementId, (start: any, end: any) => {
                const range: WalletDateRange = {
                    start: start.format("YYYY-MM-DD"),
                    end: end.format("YYYY-MM-DD"),
                    label: `${start.format("DD/MM/YYYY")} - ${end.format("DD/MM/YYYY")}`
                }
                customSet(range)
                writeSaved(storageKey, { id: "custom", range })
                onChange(range)
            })
            // Re-emit a previously saved custom range on mount.
            if (custom) {
                writeSaved(storageKey, { id: "custom", range: custom })
                onChange(custom)
            }
            return
        }

        const opt = options.find(o => o.value === dateId)
        if (opt?.range) {
            customSet(undefined)
            writeSaved(storageKey, { id: dateId })
            onChange(opt.range())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateId])


    return (
        <FlexRow>
            <div className="w-200px">
                <select className="form-select form-select-sm form-inputs"
                    value={dateId}
                    onChange={e => dateIdSet(e.target.value)}>
                    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            </div>

            {
                dateId === "custom" &&
                <div className="w-300px ms-2">
                    <div className="input-group input-group-sm" id={elementId}>
                        <input type="text" className="form-control form-control-sm" readOnly
                            value={custom ? `${custom.start} → ${custom.end}` : "Select dates"} />
                        <span className="input-group-text">
                            <i className="fas fa-calendar"></i>
                        </span>
                    </div>
                </div>
            }
        </FlexRow>
    )
}
